import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { faRoute } from "@fortawesome/pro-solid-svg-icons/faRoute";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

import { RouteTravelMode } from "@mapmycustomers/shared/enum/routes/RouteTravelMode";
import { RouteType } from "@mapmycustomers/shared/enum/routes/RouteType";
import Route from "@mapmycustomers/shared/types/entity/Route";
import GeoPoint from "@mapmycustomers/shared/types/shapes/GeoPoint";
import { Modal } from "@mapmycustomers/ui";

import AllottedTime from "@app/component/input/CreateRoute/AllottedTime";
import EndingLocation from "@app/component/input/CreateRoute/EndingLocation";
import RouteName from "@app/component/input/CreateRoute/RouteName";
import StartingLocation from "@app/component/input/CreateRoute/StartingLocation";
import VisitOrder from "@app/component/input/CreateRoute/VisitOrder";
import { reverseGeocodeAddress } from "@app/store/location/actions";
import { SavedLocationSelectSurface } from "@app/util/analytic/savedLocationsAnalytics";
import { TOPMOST_MODAL_ZINDEX } from "@app/util/consts";

import getDefaultStartDate from "../../util/route/getDefaultStartDate";
import StartDateTime from "../input/CreateRoute/StartDateTime";

import styles from "./CreateRouteModal.module.scss";
import { CreateRouteFormPayload } from "./store/actions";

interface Props {
  createOnly?: boolean;
  creating?: boolean;
  onCreate: (route: CreateRouteFormPayload) => void;
  onHide: () => void;
  onReverseGeocodeAddress: typeof reverseGeocodeAddress;
  routes: Pick<Route, "name">[];
  surface?: SavedLocationSelectSurface;
}

const CreateRoutePureModal = ({
  createOnly,
  creating,
  onCreate,
  onHide,
  onReverseGeocodeAddress,
  routes,
  surface,
}: Props) => {
  const intl = useIntl();
  const [name, setName] = useState("");
  const [visitOrder, setVisitOrder] = useState<RouteType>(RouteType.OPTIMIZED);
  const [allottedTime, setAllottedTime] = useState<number>(0);
  const [startTime, setStartTime] = useState<Date>(getDefaultStartDate());
  const [nameError, setNameError] = useState<string | undefined>();
  const [startAddressEmptyError, setStartAddressEmptyError] = useState<string | undefined>();
  const [startAddress, setStartAddress] = useState("");
  const [startGeoPoint, setStartGeoPoint] = useState<GeoPoint>();
  const [endAddress, setEndAddress] = useState("");
  const [endGeoPoint, setEndGeoPoint] = useState<GeoPoint>();

  const handleStartAddressChange = useCallback((address: string, geoPoint?: GeoPoint) => {
    setStartAddress(address);
    setStartGeoPoint(geoPoint);
  }, []);
  const handleEndAddressChange = useCallback((address: string, geoPoint?: GeoPoint) => {
    setEndAddress(address);
    setEndGeoPoint(geoPoint);
  }, []);

  const trimmedName = name.trim();
  const isDuplicateName = routes.some(({ name }) => name === trimmedName);
  const isValidRoute = !isDuplicateName && visitOrder && allottedTime !== undefined;
  const handleCreateClick = useCallback(() => {
    let allowSave = true;
    setNameError(undefined);
    setStartAddressEmptyError(undefined);
    if (name.trim().length === 0) {
      setNameError(
        intl.formatMessage({
          id: "createRouteModal.name.emptyError",
          defaultMessage: "Name should not be empty",
          description: "Empty name error of Create Route modal",
        })
      );
      allowSave = false;
    }
    if (startAddress.trim().length === 0) {
      setStartAddressEmptyError(
        intl.formatMessage({
          id: "createRouteModal.startAddress.emptyError",
          defaultMessage: "Start address should not be empty",
          description: "Empty address error of Create Route modal",
        })
      );
      allowSave = false;
    }
    if (!allowSave) {
      return;
    }

    onCreate({
      name,
      routeDetail: {
        allottedTime,
        endAddress,
        endGeoPoint,
        startAddress,
        startAt: startTime.toISOString(),
        startGeoPoint,
        travelMode: RouteTravelMode.DRIVE,
        type: visitOrder,
      },
    });
  }, [
    allottedTime,
    endAddress,
    endGeoPoint,
    intl,
    name,
    onCreate,
    setNameError,
    setStartAddressEmptyError,
    startAddress,
    startGeoPoint,
    startTime,
    visitOrder,
  ]);

  return (
    <Modal
      okButtonProps={{
        disabled: !isValidRoute || creating,
        icon: <FontAwesomeIcon icon={faRoute} />,
        loading: creating,
      }}
      okText={
        createOnly
          ? intl.formatMessage({
              id: "createRouteModal.footer.createOnly",
              defaultMessage: "Create Route",
              description: "Create button in a footer of Create Route modal",
            })
          : intl.formatMessage({
              id: "createRouteModal.footer.create",
              defaultMessage: "Create & Add to Route",
              description: "Create button in a footer of Create Route modal",
            })
      }
      onCancel={onHide}
      onOk={handleCreateClick}
      open
      scrollable
      title={intl.formatMessage({
        id: "createRouteModal.header",
        defaultMessage: "Create Route",
        description: "Header of Create Route modal",
      })}
      zIndex={TOPMOST_MODAL_ZINDEX}
    >
      <section className={styles.content}>
        <RouteName
          error={nameError}
          isDuplicateName={isDuplicateName}
          onChange={setName}
          value={name}
        />
        <VisitOrder onChange={setVisitOrder} value={visitOrder} />
        <StartDateTime onChange={setStartTime} value={startTime} />
        <AllottedTime onChange={setAllottedTime} value={allottedTime} />
        <StartingLocation
          error={startAddressEmptyError}
          onChange={handleStartAddressChange}
          onReverseGeocodeAddress={onReverseGeocodeAddress}
          surface={surface}
          value={startAddress}
        />
        <EndingLocation
          onChange={handleEndAddressChange}
          onReverseGeocodeAddress={onReverseGeocodeAddress}
          surface={surface}
          value={endAddress}
        />
      </section>
    </Modal>
  );
};

const mapDispatchToProps = {
  onReverseGeocodeAddress: reverseGeocodeAddress,
};

export default connect(null, mapDispatchToProps)(CreateRoutePureModal);
